import { addTimezoneToMoment, adjustStudyTime } from '@/shared/lib';
import axios from 'axios';
import dayjs from 'dayjs';
import _ from 'lodash';

/**
 * Attaches to each hourly summary the sample index range it contributes to an ECG window.
 * Deep-clones the summaries and, from the bytes per second of all channels, computes each
 * summary's start and stop sample offsets clipped to the window's start and stop.
 *
 * @param summaries - Hourly summaries overlapping the window.
 * @param start - Start of the ECG window.
 * @param stop - Stop of the ECG window.
 * @param samplingFrequency - Samples per second of the ECG signal.
 * @param channels - ECG channels recorded by the device.
 * @returns The cloned summaries, each carrying a dataRange with start and stop sample indexes.
 */
const addDataRanges = ({ summaries, start, stop, samplingFrequency, channels }: any) => {
  let currentLastMoment = dayjs(start);
  const stopMoment = dayjs(stop);
  const channelLength = channels.length;
  const samplePerSecond = channelLength * 2 * samplingFrequency;

  const clonedSummaries = _.cloneDeep(summaries);

  for (let i = 0; i < clonedSummaries.length; i += 1) {
    const hourlySummary = clonedSummaries[i];
    const currentStartMoment = dayjs(hourlySummary.start);
    const currentStopMoment = dayjs(hourlySummary.stop);

    const startDiff = currentLastMoment.diff(currentStartMoment, 's');
    const stopDiff = stopMoment.diff(currentStopMoment, 's');

    const startSampleIdx = startDiff >= 0 ? startDiff * samplePerSecond : 0;
    const currentStart = startDiff >= 0 ? currentLastMoment : currentStartMoment;
    let stopSampleIdx = startSampleIdx;
    if (stopDiff >= 0) {
      stopSampleIdx += currentStopMoment.diff(currentStart, 's') * samplePerSecond - 1;
    } else {
      stopSampleIdx += stopMoment.diff(currentStart, 's') * samplePerSecond - 1;
    }

    currentLastMoment = currentStopMoment;
    hourlySummary.dataRange = {
      start: startSampleIdx,
      stop: stopSampleIdx,
    };
  }

  return clonedSummaries;
};

/**
 * Picks the hourly ECG summaries that overlap a requested time window. Returns an empty list when
 * there are no summaries, filters the rest by epoch overlap with the window, and optionally adds
 * each summary's sample data range through addDataRanges.
 *
 * @param summaries - Hourly summary response whose data holds the entries.
 * @param start - Start of the window.
 * @param stop - Stop of the window.
 * @param samplingFrequency - Samples per second of the ECG signal.
 * @param channels - ECG channels recorded by the device.
 * @param willAddDataRanges - Whether sample data ranges are added to the matches.
 * @returns The overlapping summaries, with data ranges when requested.
 */
export const filterHourlySummaryRanges = ({
  summaries,
  start,
  stop,
  samplingFrequency,
  channels,
  willAddDataRanges,
}: any) => {
  if (!summaries?.data?.length) {
    return [];
  }

  const startEpoch = start.valueOf();
  const stopEpoch = stop.valueOf();
  /**
   * Selects the summary entries whose start/stop epochs overlap the requested start/stop window. It filters summaries.data using dayjs-parsed epochs against startEpoch and stopEpoch.
   *
   * @returns The summary entries overlapping the requested time window.
   */
  const ranges = _.filter(summaries.data, (x) => {
    const itemStartEpoch = dayjs(x.start).valueOf();
    const itemStopEpoch = dayjs(x.stop).valueOf();
    return (
      (itemStartEpoch <= startEpoch && itemStopEpoch > startEpoch && itemStopEpoch < stopEpoch) ||
      (itemStartEpoch <= startEpoch && itemStopEpoch >= stopEpoch) ||
      (itemStartEpoch > startEpoch && itemStartEpoch < stopEpoch)
    );
  });

  if (willAddDataRanges) {
    return addDataRanges({
      summaries: ranges,
      start,
      stop,
      samplingFrequency,
      channels,
    });
  }

  return ranges;
};

/**
 * Computes how many minutely heart-rate samples a study window should contain. Takes the whole-minute difference between the two instants and adds one when the stop instant carries leftover seconds.
 *
 * @param start - Start of the window.
 * @param stop - End of the window.
 * @returns The expected number of minutely heart rate entries.
 */
const getExpectedMinutelyHrLength = (start: any, stop: any) => {
  const startMoment = dayjs(start);
  const stopMoment = dayjs(stop);
  const diffMin = stopMoment.diff(startMoment, 'm');
  return diffMin + (stopMoment.get('s') > 0 ? 1 : 0);
};

/**
 * Lays out minutely heart-rate values on a per-minute timeline for a study window. Fills an array
 * sized to the window with null, then copies each hourly summary's heart rates into the matching
 * slots, offset from the summary start adjusted to study time and rounded up to the minute.
 *
 * @param hourlySummaries - Hourly summaries whose starts align the heart-rate lists.
 * @param minutelyHrs - Minutely heart-rate lists, one per hourly summary.
 * @param start - Start of the window.
 * @param stop - Stop of the window.
 * @param studyTimeOffset - Study timezone offset applied to the summary starts.
 * @returns One heart-rate value per minute of the window, null where no data exists.
 */
export const createMinutelyHrChartData = ({
  hourlySummaries,
  minutelyHrs,
  start,
  stop,
  studyTimeOffset,
}: any) => {
  const startMoment = dayjs(start);
  const expectedLength = getExpectedMinutelyHrLength(start, stop);
  /**
   * Builds a fixed-length array of null placeholders sized to the expected minutely heart-rate length. It uses lodash times over expectedLength to fill each slot with null.
   *
   * @returns An array of expectedLength null entries.
   */
  const result = _.times(expectedLength, (): any => null);

  if (minutelyHrs.length > 0) {
    for (let i = 0; i < hourlySummaries.length; i += 1) {
      const hourlySummary = hourlySummaries[i];
      const hourlySummaryStartMoment = adjustStudyTime({
        dateMoment: dayjs(hourlySummary.start),
        studyTimeOffset,
      });
      // *: Round start date
      if (!hourlySummaryStartMoment.isSame(hourlySummaryStartMoment.clone().startOf('minute'))) {
        hourlySummaryStartMoment.add(1, 'minute');
      }
      const hrs = minutelyHrs[i];
      if (hrs) {
        const startDiff = startMoment.diff(hourlySummaryStartMoment, 'm');
        const resultIdx = startDiff >= 0 ? 0 : -startDiff;
        const hrIdx = startDiff >= 0 ? startDiff : 0;
        let currentIdx = resultIdx;
        for (let ii = hrIdx; ii < hrs.length && currentIdx < expectedLength; ii += 1) {
          result[currentIdx] = Number(hrs[ii]);
          currentIdx += 1;
        }
      }
    }
  }

  return result;
};

/**
 * Deinterleaves a flat binary ECG sample buffer into one array per channel. Walks the buffer in strides of totalChannel pushing each sample onto its channel array, and yields undefined when no binary data is supplied.
 *
 * @param totalChannel - Number of interleaved ECG channels.
 * @param binaryData - Flat buffer of interleaved samples.
 * @returns One sample array per channel, or undefined without input data.
 */
export const convertBinaryToECGData = (totalChannel: any, binaryData: any) => {
  if (binaryData) {
    const ecgData = new Array(totalChannel);
    for (let i = 0; i < binaryData.length; i += totalChannel) {
      for (let j = 0; j < totalChannel; j += 1) {
        if (ecgData[j]) {
          ecgData[j].push(binaryData[i + j]);
        } else {
          ecgData[j] = [binaryData[i + j]];
        }
      }
    }
    return ecgData;
  }
  return undefined;
};

/**
 * Builds the series of a one-day heart-rate chart, including missing-data shading and event
 * markers. Walks the 1440 minutes of the selected day, pushing each heart rate or a null, marking
 * the edges of missing stretches at maxHr, and placing event guides at minutes where events start.
 *
 * @param hrArray - Heart-rate values indexed by minute of the day.
 * @param eventIndicators - Events whose start minutes get a guide.
 * @param selectedDate - Day the chart covers.
 * @param maxHr - Heart-rate ceiling used for guides and missing-data points.
 * @param studyTimeOffset - Study timezone offset applied to event starts.
 * @returns An object carrying the hrs, guides, eventGuides, missingPoints, hrIndexes and eventIndexes series.
 */
export const createChartData = ({
  hrArray,
  eventIndicators,
  selectedDate,
  maxHr,
  studyTimeOffset,
}: any): any => {
  const firstEpoch = selectedDate.startOf('day').valueOf();

  const hrs: any = [];
  const guides: any = [];
  const eventGuides: any = [];
  const missingPoints: any = [];

  const hrIndexes: any = [];

  /**
   * Maps each event indicator to its minute offset from the study's first epoch, rounding the event start down to the minute (or up when it starts mid-minute) after applying the study time offset. It uses dayjs and adjustStudyTime to normalize each start before computing the minute distance.
   *
   * @returns The per-event minute offsets from the first epoch.
   */
  const eventIndexes = _.map(eventIndicators, (eventIndicator) => {
    const roundMoment =
      dayjs(eventIndicator.start).second() === 0
        ? adjustStudyTime({
            dateMoment: dayjs(eventIndicator.start),
            studyTimeOffset,
          }).startOf('minute')
        : adjustStudyTime({
            dateMoment: dayjs(eventIndicator.start).add(1, 'minute'),
            studyTimeOffset,
          }).startOf('minute');
    return (roundMoment.valueOf() - firstEpoch) / 1000 / 60;
  });

  _.forEach(_.range(1440), (x): any => {
    const epoch = firstEpoch + x * 60000;

    const hr = hrArray[x];
    if (_.isNil(hr)) {
      hrs.push([epoch, null]);

      missingPoints.push([epoch, maxHr]);

      if (!_.isNil(hrArray[x - 1])) {
        // *: Previous point has HR value
        missingPoints.splice(x - 1, 1, [epoch - 60000, maxHr]);
      }
    } else {
      hrs.push([epoch, hr]);

      hrIndexes.push({
        index: x,
        category: epoch,
        avgHr: hr,
      });

      if (_.isNil(hrArray[x - 1])) {
        // *: Previous point do not have HR value so add current point to missingPoints array
        missingPoints.push([epoch, maxHr]);
      } else {
        missingPoints.push([epoch, null]);
      }
    }

    guides.push([epoch, maxHr, false]);
    if (_.includes(eventIndexes, x)) {
      eventGuides.push([epoch, maxHr]);
    } else {
      eventGuides.push([epoch, null]);
    }
  });

  return {
    hrs,
    guides,
    eventGuides,
    missingPoints,
    hrIndexes,
    eventIndexes,
  };
};

/**
 * Downloads the raw ECG sample bytes of the selected time ranges from storage. Issues one ranged axios arraybuffer GET per range with the signature appended, converts every response into an Int16Array and concatenates them into a single buffer, returning an empty array for no ranges or on a logged failure.
 *
 * @param ranges - Selected ECG ranges carrying a data url and byte range.
 * @param querySignature - Signed query string authorizing the storage reads.
 * @returns A single Int16Array of concatenated samples, or an empty array.
 */
export const downloadSelectedEcgData = async (ranges: any, querySignature: any): Promise<any> => {
  try {
    if (ranges.length) {
      const promises: any = [];
      _.forEach(ranges, (range) => {
        promises.push(
          axios.get(`${range.dataUrl}?${querySignature}`, {
            responseType: 'arraybuffer',
            headers: {
              Range: `bytes=${range.dataRange.start}-${range.dataRange.stop}`,
            },
          }),
        );
      });
      const response: any = await Promise.all(promises);
      /**
       * Converts each downloaded range response into an Int16Array of ECG samples. It maps over the response collection reading each entry's data buffer.
       *
       * @returns An array of Int16Array sample buffers, one per response.
       */
      const selectedEcgData = _.map(response, (x) => new Int16Array(x.data));
      return new Int16Array(
        selectedEcgData.reduce((acc, curr) => [...acc, ...curr], [] as number[]),
      );
    }
    return [];
  } catch (error) {
    console.error('Failed to download selected ECG data: ', error);
    return [];
  }
};

/**
 * Builds the synthetic sine wave that fills gaps in an ECG strip, sized to the missing duration. Generates one base cycle scaled to 0.75 of half the ADC gain, splits it into a positive half shifted down and a negative half shifted up by 1.25 times the gain, and reports how many whole cycles and samples the gap needs.
 *
 * @param diffSeconds - Length of the gap in seconds.
 * @param samplingFrequency - Samples per second of the ECG signal.
 * @param adcGain - ADC gain used to scale the wave amplitude.
 * @returns The cycle count, the positive and negative half-cycle arrays and the missing sample length.
 */
export const getSineWaveDataArray = (diffSeconds: any, samplingFrequency: any, adcGain: any) => {
  const halfSamplingFrequency = samplingFrequency / 2;

  const missingSampleLength = diffSeconds * samplingFrequency;
  const sineWaveCount = Math.floor(missingSampleLength / samplingFrequency);

  /**
   * Generates one full base sine-wave cycle spanning the sampling frequency, with amplitude scaled to 0.75 of half the ADC gain. It maps over a range of samplingFrequency points evaluating the sine function.
   *
   * @returns One cycle of base sine-wave sample values.
   */
  const sineWaveDataArray = _.map(
    _.range(samplingFrequency),
    (x) => Math.sin(x * ((2 * Math.PI) / samplingFrequency)) * (0.75 * (adcGain / 2)),
  );

  // *: Create positive & negative sinewave with translating y-offset by 0.75 * adcGain (1.5 square)
  /**
   * Builds the positive half-cycle of the synthetic sine wave by rounding the first half of the base sine samples and translating them down by 1.25 times the ADC gain. It maps over the lower slice of sineWaveDataArray.
   *
   * @returns The positive half-cycle sine-wave sample values.
   */
  const positiveSineWaveDataArray = _.map(sineWaveDataArray.slice(0, halfSamplingFrequency), (x) =>
    Math.round(x - 1.25 * adcGain),
  );
  /**
   * Builds the negative half-cycle of the synthetic sine wave by rounding the second half of the base sine samples and translating them up by 1.25 times the ADC gain. It maps over the upper slice of sineWaveDataArray.
   *
   * @returns The negative half-cycle sine-wave sample values.
   */
  const negativeSineWaveDataArray = _.map(
    sineWaveDataArray.slice(halfSamplingFrequency, sineWaveDataArray.length),
    (x) => Math.round(x + 1.25 * adcGain),
  );

  return {
    sineWaveCount,
    positiveSineWaveDataArray,
    negativeSineWaveDataArray,
    missingSampleLength,
  };
};

/**
 * Checks whether an ECG data map holds a usable stretch of signal. Iterates the entries and flags the map valid as soon as one range spans at least 60 seconds between its start and stop.
 *
 * @param data - ECG data map entries to inspect.
 * @returns True when at least one range covers a minute or more.
 */
export const validateEcgDataMap = (data: any) => {
  let isValidData = false;
  _.forEach(data, (item) => {
    const diffSeconds = dayjs(item.stop).diff(dayjs(item.start), 'second');
    if (diffSeconds >= 60) {
      isValidData = true;
    }
  });
  return isValidData;
};

/**
 * Fills the gaps between downloaded ECG ranges with synthetic sine waves so a strip spans its whole window. Returns the data untouched once a channel already holds 75000 samples, otherwise walks every channel and range, measures the timezone-adjusted gaps before the first, between consecutive and after the last range, and splices in the half-cycles from getSineWaveDataArray.
 *
 * @param selectedEcgData - Downloaded per-channel ECG samples.
 * @param ranges - Ranges the samples were downloaded for.
 * @param startMoment - Requested start of the strip.
 * @param stopMoment - Requested stop of the strip.
 * @param ecgDataMap - Data map supplying the sampling frequency and gain.
 * @returns The gap-filled channel data plus the leading and trailing missing sample lengths.
 */
export const addSineWaveToEcgData = (
  selectedEcgData: any,
  ranges: any,
  startMoment: any,
  stopMoment: any,
  ecgDataMap: any,
): any => {
  let missingSampleLengthPre = 0;
  let missingSampleLengthPost = 0;

  if (selectedEcgData[0].length === 75000) {
    // *: Enough 5 minutes ECG data
    return {
      ecgData: selectedEcgData,
      missingSampleLengthPre,
      missingSampleLengthPost,
    };
  }

  const { samplingFrequency, gain } = ecgDataMap;

  const ecgData = [];

  for (let j = 0; j < selectedEcgData.length; j += 1) {
    let pushECGData: any = [];

    for (let i = 0; i < ranges.length; i += 1) {
      const currentStartMoment = addTimezoneToMoment({
        dateMoment: dayjs(ranges[i].start),
      });
      const currentStopMoment = addTimezoneToMoment({
        dateMoment: dayjs(ranges[i].stop),
      });

      if (!ranges[i - 1]) {
        // *: First
        const preDiffSeconds = currentStartMoment?.diff(startMoment, 'second') ?? 0;
        if (preDiffSeconds > 0) {
          // *: Missing pre data
          const {
            sineWaveCount,
            positiveSineWaveDataArray,
            negativeSineWaveDataArray,
            missingSampleLength: missingSampleLengthTmp,
          } = getSineWaveDataArray(preDiffSeconds, samplingFrequency, gain);
          missingSampleLengthPre = missingSampleLengthTmp;

          // *: Each sineWaveCount has couple of positive & negative sinewave
          for (let k = 0; k < sineWaveCount; k += 1) {
            pushECGData = pushECGData.concat(positiveSineWaveDataArray);
            pushECGData = pushECGData.concat(negativeSineWaveDataArray);
          }
        }

        pushECGData = pushECGData.concat(selectedEcgData[j]);

        if (ranges.length === 1) {
          // *: Only 1 range
          const postDiffSeconds = stopMoment.diff(currentStopMoment, 'second');
          if (postDiffSeconds > 0) {
            // *: Missing post data
            const {
              sineWaveCount,
              positiveSineWaveDataArray,
              negativeSineWaveDataArray,
              missingSampleLength: missingSampleLengthTmp,
            } = getSineWaveDataArray(postDiffSeconds, samplingFrequency, gain);
            missingSampleLengthPost = missingSampleLengthTmp;
            for (let k = 0; k < sineWaveCount; k += 1) {
              pushECGData = pushECGData.concat(positiveSineWaveDataArray);
              pushECGData = pushECGData.concat(negativeSineWaveDataArray);
            }
          }
        }
      } else {
        // *: Other
        const previousStopMoment = addTimezoneToMoment({
          dateMoment: dayjs(ranges[i - 1].stop),
        });
        const preDiffSeconds = currentStartMoment?.diff(previousStopMoment, 'second') ?? 0;
        if (preDiffSeconds > 0) {
          // *: Missing pre data
          const { sineWaveCount, positiveSineWaveDataArray, negativeSineWaveDataArray } =
            getSineWaveDataArray(preDiffSeconds, samplingFrequency, gain);
          for (let k = 0; k < sineWaveCount; k += 1) {
            pushECGData = pushECGData.concat(positiveSineWaveDataArray);
            pushECGData = pushECGData.concat(negativeSineWaveDataArray);
          }
        }

        pushECGData = pushECGData.concat(selectedEcgData[j]);

        if (!ranges[i + 1]) {
          // *: Last
          const postDiffSeconds = stopMoment.diff(currentStopMoment, 'second');
          if (postDiffSeconds > 0) {
            // *: Missing post data
            const { sineWaveCount, positiveSineWaveDataArray, negativeSineWaveDataArray } =
              getSineWaveDataArray(postDiffSeconds, samplingFrequency, gain);
            for (let k = 0; k < sineWaveCount; k += 1) {
              pushECGData = pushECGData.concat(positiveSineWaveDataArray);
              pushECGData = pushECGData.concat(negativeSineWaveDataArray);
            }
          }
        }
      }
    }
    ecgData.push(pushECGData);
  }

  return {
    ecgData,
    missingSampleLengthPre,
    missingSampleLengthPost,
  };
};
